import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  addOrSubtractUp,
  compassLeastOrBest,
  lowerVsUpper,
  type Hand,
} from "@nav-games/compass";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface SignsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "compass";
const GAME = "signs";
const CARDS_PER_SESSION = 12;

interface Question {
  /// Template id — also the stats key.
  id: string;
  prompt: React.ReactNode;
  options: readonly [string, string];
  correct: string;
  explain: string;
}

function randHand(): Hand {
  return Math.random() < 0.5 ? "E" : "W";
}

function randDeg(): number {
  return 2 + Math.floor(Math.random() * 10);
}

const TEMPLATES: ReadonlyArray<() => Question> = [
  // CADET sign rule.
  () => {
    const hand = randHand();
    return {
      id: "addsub",
      prompt: (
        <>
          Correcting <strong>Compass → True</strong> with a {randDeg()}°
          {hand} error — add or subtract?
        </>
      ),
      options: ["Add", "Subtract"],
      correct: addOrSubtractUp(hand) === "add" ? "Add" : "Subtract",
      explain: "CADET — Compass ADd East → True; subtract westerly errors.",
    };
  },
  // Variation: magnetic vs true.
  () => {
    const hand = randHand();
    return {
      id: "mag-vs-true",
      prompt: (
        <>
          Variation {randDeg()}°{hand}. Is <strong>Magnetic</strong> greater
          or less than <strong>True</strong>?
        </>
      ),
      options: ["Greater", "Less"],
      correct: lowerVsUpper(hand) === "greater" ? "Greater" : "Less",
      explain:
        "Variation East → magnetic less than true; West → magnetic greater (best).",
    };
  },
  // Deviation: compass vs magnetic.
  () => {
    const hand = randHand();
    return {
      id: "comp-vs-mag",
      prompt: (
        <>
          Deviation {randDeg()}°{hand}. Is <strong>Compass</strong> greater or
          less than <strong>Magnetic</strong>?
        </>
      ),
      options: ["Greater", "Less"],
      correct: lowerVsUpper(hand) === "greater" ? "Greater" : "Less",
      explain:
        "Deviation East → compass less than magnetic; West → compass greater (best).",
    };
  },
  // Error East/West → compass least/best.
  () => {
    const hand = randHand();
    return {
      id: "least-best",
      prompt: (
        <>
          The combined error is <strong>{hand === "E" ? "East" : "West"}</strong>.
          Does the compass read least or best?
        </>
      ),
      options: ["Least", "Best"],
      correct: compassLeastOrBest(hand) === "least" ? "Least" : "Best",
      explain: "Error East, Compass Least; Error West, Compass Best.",
    };
  },
];

function nextQuestion(prevId?: string): Question {
  let q = TEMPLATES[Math.floor(Math.random() * TEMPLATES.length)]!();
  // Avoid the same template twice running so it doesn't feel repetitive.
  let guard = 0;
  while (q.id === prevId && TEMPLATES.length > 1 && guard < 8) {
    q = TEMPLATES[Math.floor(Math.random() * TEMPLATES.length)]!();
    guard += 1;
  }
  return q;
}

export function CompassSigns({ onNavigate }: SignsProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [question, setQuestion] = useState<Question>(() => nextQuestion());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setQuestion((q) => nextQuestion(q.id));
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === question.correct;
      const attempt: Omit<Attempt, "module"> = {
        char: question.id,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [chosen, question, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setQuestion(nextQuestion());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/chartwork")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "compass" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <p style={{ marginTop: 0, fontSize: 18 }}>{question.prompt}</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {question.options.map((opt) => {
            const isCorrect = opt === question.correct;
            const isChosen = opt === chosen;
            return (
              <button
                key={opt}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(opt)}
                style={{
                  fontSize: 18,
                  padding: "14px 0",
                  fontWeight: 600,
                  background: revealed
                    ? isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : isChosen
                        ? "rgba(184, 48, 42, 0.12)"
                        : undefined
                    : undefined,
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div role="status" aria-live="polite" style={{ marginTop: 12 }}>
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === question.correct ? "✓ Correct." : `✗ It's ${question.correct}.`}
            </p>
            <p className="small muted" style={{ margin: "6px 0 0" }}>
              {question.explain}
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/chartwork")}>
        ← Back to Chartwork
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Chartwork
        </button>
      </div>
    </>
  );
}
